// DNA CONNECT - Módulo Pedagógico (Notas Parciais e Boletim Separados)
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  GraduationCap, BookOpen, Calendar, AlertCircle, CheckCircle,
  Loader2, User, TrendingUp, TrendingDown, Clock, Info,
  Calculator, FileText, ClipboardCheck, PenTool, HelpCircle, 
  ChevronRight, ListChecks, Award, LifeBuoy
} from 'lucide-react';
import { toast } from 'sonner';
import BoletimPremium from './BoletimPremium';
import AulasExtrasFamilia from './AulasExtrasFamilia';

const API_URL = window.location.origin;

// Definição dos tipos de avaliação com explicações
const TIPOS_AVALIACAO = {
  'APA': {
    nome: 'APA',
    titulo: 'Avaliação de Produção do Aluno',
    descricao: 'Avalia a produção individual do aluno em sala de aula, incluindo exercícios, atividades práticas e participação nas tarefas propostas.',
    peso: '25%',
    nota_max: '10,0',
    icon: PenTool,
    cor: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700'
  },
  'Prontidão': {
    nome: 'Prontidão',
    titulo: 'Avaliação de Prontidão',
    descricao: 'Avalia o comprometimento do aluno: pontualidade, entrega de tarefas de casa, organização do material e participação nas aulas.',
    peso: '25%',
    nota_max: '10,0',
    icon: ClipboardCheck,
    cor: 'from-green-500 to-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700'
  },
  'Trabalho': {
    nome: 'Trabalho',
    titulo: 'Trabalhos e Projetos',
    descricao: 'Avalia trabalhos individuais ou em grupo, projetos, pesquisas e apresentações realizadas durante o trimestre.',
    peso: '25%',
    nota_max: '10,0',
    icon: FileText,
    cor: 'from-orange-500 to-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700'
  },
  'Avaliação Trimestral': {
    nome: 'Av. Trimestral',
    titulo: 'Avaliação Trimestral',
    descricao: 'Prova formal que avalia o conteúdo completo estudado no período, aplicada ao final de cada trimestre.',
    peso: '25%',
    nota_max: '10,0',
    icon: GraduationCap,
    cor: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700'
  }
};

// Cores por disciplina para a Semana de Avaliações
const CORES_DISCIPLINAS = {
  'Matemática': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  'Português': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  'Língua Portuguesa': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  'História': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  'Geografia': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  'Ciências': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
  'Física': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  'Química': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  'Biologia': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  'Inglês': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
  'Artes': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
  'Arte': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
  'Educação Física': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  'Filosofia': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  'Música': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
};

const getCorDisciplina = (nome) => {
  return CORES_DISCIPLINAS[nome] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
};

// Componente da aba Semana de Avaliações
function SemanaAvaliacoesTab({ filhoId, turmaId }) {
  const [loading, setLoading] = useState(true);
  const [semanas, setSemanas] = useState([]);
  const [viewMode, setViewMode] = useState('grade'); // 'grade' ou 'lista'

  useEffect(() => {
    if (turmaId) {
      fetchSemanaAvaliacoes();
    }
  }, [turmaId]);

  const fetchSemanaAvaliacoes = async () => {
    try {
      setLoading(true);
      
      // Usar endpoint simplificado que busca direto pelo turma_id
      const response = await fetch(
        `${API_URL}/api/assessment-week/connect/avaliacoes-por-turma?turma_id=${turmaId}`
      );
      
      const data = await response.json().catch(() => ({ semanas: [] }));
      setSemanas(data.semanas || []);
      
    } catch (error) {
      console.error('Erro ao carregar semana de avaliações:', error);
      setSemanas([]);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const d = new Date(dataStr + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long' 
    });
  };

  const formatarDiaSemana = (dataStr) => {
    if (!dataStr) return '';
    const d = new Date(dataStr + 'T12:00:00');
    const dia = d.toLocaleDateString('pt-BR', { weekday: 'long' });
    return dia.charAt(0).toUpperCase() + dia.slice(1);
  };

  const formatarDiaMes = (dataStr) => {
    if (!dataStr) return '';
    const d = new Date(dataStr + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  // Organizar avaliações para visualização em grade
  const organizarParaGrade = (semana) => {
    const avaliacoesPorData = semana.avaliacoes_por_data || {};
    const datas = Object.keys(avaliacoesPorData).sort();
    
    // Extrair todos os horários únicos
    const horariosSet = new Set();
    Object.values(avaliacoesPorData).forEach(avaliacoes => {
      avaliacoes.forEach(aval => {
        if (aval.horario) {
          horariosSet.add(aval.horario);
        }
      });
    });
    
    const horarios = Array.from(horariosSet).sort((a, b) => {
      const horaA = a.split(' - ')[0] || '00:00';
      const horaB = b.split(' - ')[0] || '00:00';
      return horaA.localeCompare(horaB);
    });

    return { datas, horarios, avaliacoesPorData };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  if (semanas.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <BookOpen className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Nenhuma Semana de Avaliações
          </h3>
          <p className="text-gray-500 text-center text-sm">
            Não há semana de avaliações publicada para esta turma no momento.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {semanas.map((semana) => {
        const { datas, horarios, avaliacoesPorData } = organizarParaGrade(semana);
        
        return (
          <Card key={semana.id} className="overflow-hidden">
            {/* Header da Semana */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-lg font-bold">{semana.trimestre}º Trimestre {semana.ano}</h3>
                  <p className="text-purple-200 text-sm">{semana.turma}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-white/20 text-white border-white/30">
                    <Calendar className="h-3 w-3 mr-1" />
                    {formatarDiaMes(semana.data_inicio)} - {formatarDiaMes(semana.data_fim)}
                  </Badge>
                </div>
              </div>
              
              {/* Botões de visualização */}
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant={viewMode === 'grade' ? 'secondary' : 'ghost'}
                  onClick={() => setViewMode('grade')}
                  className={viewMode === 'grade' ? 'bg-white text-purple-700' : 'text-white hover:bg-white/20'}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Grade
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'lista' ? 'secondary' : 'ghost'}
                  onClick={() => setViewMode('lista')}
                  className={viewMode === 'lista' ? 'bg-white text-purple-700' : 'text-white hover:bg-white/20'}
                >
                  <ListChecks className="h-4 w-4 mr-1" />
                  Lista
                </Button>
              </div>
            </div>

            <CardContent className="p-0">
              {/* VISUALIZAÇÃO EM GRADE */}
              {viewMode === 'grade' && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left text-sm font-semibold text-gray-700 w-20">
                          Horário
                        </th>
                        {datas.map(data => (
                          <th key={data} className="border p-2 text-center text-sm font-semibold text-gray-700">
                            <div className="text-purple-700">{formatarDiaSemana(data)}</div>
                            <div className="text-xs text-gray-500">{formatarDiaMes(data)}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {horarios.map(horario => (
                        <tr key={horario} className="hover:bg-gray-50">
                          <td className="border p-2 text-xs text-gray-600 font-medium whitespace-nowrap">
                            {horario.split(' - ')[0]}
                            <br />
                            <span className="text-gray-400">{horario.split(' - ')[1]}</span>
                          </td>
                          {datas.map(data => {
                            const avaliacoes = avaliacoesPorData[data] || [];
                            const aval = avaliacoes.find(a => a.horario === horario);
                            
                            if (aval) {
                              const cor = getCorDisciplina(aval.disciplina);
                              return (
                                <td key={data} className={`border p-2 ${cor.bg}`}>
                                  <div className={`font-bold text-sm ${cor.text}`}>
                                    {aval.disciplina}
                                  </div>
                                  {aval.professor && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      {aval.professor}
                                    </div>
                                  )}
                                </td>
                              );
                            }
                            
                            return (
                              <td key={data} className="border p-2 bg-gray-50">
                                <span className="text-gray-300 text-xs">—</span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* VISUALIZAÇÃO EM LISTA */}
              {viewMode === 'lista' && (
                <>
                  {Object.entries(avaliacoesPorData).map(([data, avaliacoes]) => (
                    <div key={data} className="border-b last:border-b-0">
                      {/* Data */}
                      <div className="bg-gray-50 px-4 py-2 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-gray-900 capitalize text-sm">
                          {formatarData(data)}
                        </span>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {avaliacoes.length} {avaliacoes.length === 1 ? 'prova' : 'provas'}
                        </Badge>
                      </div>

                      {/* Avaliações do dia */}
                      <div className="p-3 grid gap-2 md:grid-cols-2">
                        {avaliacoes.map((aval, idx) => {
                          const cor = getCorDisciplina(aval.disciplina);
                          return (
                            <div 
                              key={idx}
                              className={`${cor.bg} ${cor.border} border rounded-lg p-3`}
                            >
                              <h4 className={`font-bold ${cor.text}`}>{aval.disciplina}</h4>
                              <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                                <Clock className="h-3 w-3" />
                                <span>{aval.horario}</span>
                                <span className="text-gray-400">•</span>
                                <span>{aval.aula}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function DnaConnectPedagogico() {
  const [loading, setLoading] = useState(true);
  const [filhos, setFilhos] = useState([]);
  const [selectedFilho, setSelectedFilho] = useState('');
  const [boletim, setBoletim] = useState(null);
  const [frequencia, setFrequencia] = useState(null);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [horarios, setHorarios] = useState(null); // Estado para horários
  const [activeTab, setActiveTab] = useState('parciais');
  const [selectedTrimestre, setSelectedTrimestre] = useState('1º Trimestre');
  const [showExplicacao, setShowExplicacao] = useState(false);
  const [selectedDisciplina, setSelectedDisciplina] = useState(null);
  const [submodulosHabilitados, setSubmodulosHabilitados] = useState(null);

  // Carregar submódulos habilitados
  const loadSubmodulos = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/dashboard?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        // submodulos específicos do pedagógico
        setSubmodulosHabilitados(data.submodulos_habilitados?.pedagogico || {});
      }
    } catch (error) {
      console.error('Erro ao carregar submódulos:', error);
    }
  };

  // Verificar se um submódulo está habilitado
  // Por padrão, todos estão habilitados (se não houver regra específica)
  const isSubmoduloHabilitado = (submodulo) => {
    if (!submodulosHabilitados) return true; // Se não carregou, libera tudo
    if (Object.keys(submodulosHabilitados).length === 0) return true; // Se não há regras, libera tudo
    return submodulosHabilitados[submodulo] !== false; // Só bloqueia se explicitamente false
  };

  useEffect(() => {
    loadFilhos();
    loadSubmodulos();
  }, []);

  useEffect(() => {
    if (selectedFilho) {
      loadPedagogico();
    }
  }, [selectedFilho, activeTab]);

  const loadFilhos = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/filhos?token=${token}`);
      const data = await response.json();
      setFilhos(data);
      if (data.length > 0) {
        setSelectedFilho(data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar filhos');
    }
  };

  const loadPedagogico = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('familyToken');
      
      if (activeTab === 'parciais' || activeTab === 'boletim') {
        const res = await fetch(`${API_URL}/api/dnaconnect/pedagogico/boletim/${selectedFilho}?token=${token}`);
        const responseText = await res.text();
        try {
          const data = JSON.parse(responseText);
          if (!res.ok) {
            console.error('Erro boletim:', data);
            setBoletim({ grades: [], subjects: [] });
          } else {
            setBoletim(data);
          }
        } catch (e) {
          console.error('Parse error:', e, responseText);
          setBoletim({ grades: [], subjects: [] });
        }
      } else if (activeTab === 'frequencia') {
        const res = await fetch(`${API_URL}/api/dnaconnect/pedagogico/frequencia/${selectedFilho}?token=${token}`);
        const responseText = await res.text();
        try {
          const data = JSON.parse(responseText);
          if (!res.ok) {
            console.error('Erro frequencia:', data);
            setFrequencia({ attendance: [], statistics: {} });
          } else {
            setFrequencia(data);
          }
        } catch (e) {
          console.error('Parse error:', e, responseText);
          setFrequencia({ attendance: [], statistics: {} });
        }
      } else if (activeTab === 'ocorrencias') {
        const res = await fetch(`${API_URL}/api/dnaconnect/pedagogico/ocorrencias/${selectedFilho}?token=${token}`);
        const responseText = await res.text();
        try {
          const data = JSON.parse(responseText);
          if (!res.ok) {
            console.error('Erro ocorrencias:', data);
            setOcorrencias([]);
          } else {
            setOcorrencias(data.ocorrencias || []);
          }
        } catch (e) {
          console.error('Parse error:', e, responseText);
          setOcorrencias([]);
        }
      } else if (activeTab === 'horarios') {
        const res = await fetch(`${API_URL}/api/dnaconnect/pedagogico/horarios/${selectedFilho}?token=${token}`);
        const data = await res.json();
        setHorarios(data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados pedagógicos:', error);
      toast.error('Erro ao carregar dados pedagógicos');
    } finally {
      setLoading(false);
    }
  };

  // Função para arredondar para 0,5 mais próximo
  const arredondarPara05 = (valor) => {
    if (valor === null || valor === undefined) return null;
    return Math.round(valor * 2) / 2;
  };

  const getNotaColor = (nota, max = 10) => {
    const percentual = (nota / max) * 100;
    if (percentual >= 70) return 'text-green-600 bg-green-50 border-green-200';
    if (percentual >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // Agrupar notas PARCIAIS por disciplina e período
  const getNotasParciais = (subjectId) => {
    if (!boletim?.grades) return {};
    
    const notasDisciplina = boletim.grades.filter(g => 
      g.subject_id === subjectId && 
      g.period === selectedTrimestre &&
      g.grade_type === 'parcial'
    );
    
    const notas = {};
    notasDisciplina.forEach(nota => {
      notas[nota.evaluation_type] = nota.grade_value;
    });
    
    return notas;
  };

  // Buscar nota FINAL (média calculada) por disciplina e período
  const getNotaFinal = (subjectId, periodo) => {
    if (!boletim?.grades) return null;
    
    const notaFinal = boletim.grades.find(g => 
      g.subject_id === subjectId && 
      g.period === periodo &&
      (g.grade_type === 'trimestral' || g.grade_type === 'final')
    );
    
    return notaFinal?.grade_value ?? null;
  };

  // Calcular média trimestral a partir das notas parciais (arredondada de 0,5 em 0,5)
  const calcularMediaTrimestral = (notas) => {
    const valores = Object.values(notas).filter(v => v !== undefined && v !== null);
    if (valores.length === 0) return null;
    const soma = valores.reduce((a, b) => a + b, 0);
    const media = soma / valores.length;
    return arredondarPara05(media);
  };

  // Abrir detalhes da disciplina
  const abrirDetalhesDisciplina = (subject) => {
    setSelectedDisciplina(subject);
  };

  // Obter filho selecionado
  const filhoAtual = filhos.find(f => f.id === selectedFilho);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Pedagógico</h1>
        
        {/* Seletor de Filhos - Sempre visível quando há filhos */}
        {filhos.length > 0 && (
          <Select value={selectedFilho} onValueChange={setSelectedFilho}>
            <SelectTrigger className="w-[280px] bg-white border-purple-200" data-testid="select-filho">
              <SelectValue placeholder="Selecione o filho">
                {filhoAtual && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-600" />
                    <span className="truncate">{filhoAtual.name}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {filhos.map((filho) => (
                <SelectItem key={filho.id} value={filho.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      filho.id === selectedFilho ? 'bg-purple-600' : 'bg-gray-400'
                    }`}>
                      {filho.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium">{filho.name}</span>
                      <span className="text-xs text-gray-500">{filho.class_name}</span>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Cards de Filhos - Visível quando há múltiplos filhos */}
      {filhos.length > 1 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filhos.map((filho) => (
            <Card 
              key={filho.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                filho.id === selectedFilho 
                  ? 'bg-gradient-to-r from-purple-100 to-purple-200 border-purple-400 ring-2 ring-purple-400' 
                  : 'bg-white hover:border-purple-300'
              }`}
              onClick={() => setSelectedFilho(filho.id)}
              data-testid={`filho-card-${filho.id}`}
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                  filho.id === selectedFilho ? 'bg-purple-600' : 'bg-gray-400'
                }`}>
                  {filho.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate text-sm ${
                    filho.id === selectedFilho ? 'text-purple-900' : 'text-gray-700'
                  }`}>
                    {filho.name?.split(' ')[0]}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{filho.class_name}</p>
                </div>
                {filho.id === selectedFilho && (
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Card do Aluno Selecionado */}
      {filhoAtual && (
        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center">
                <User className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-purple-900">{filhoAtual.name}</h2>
                <p className="text-purple-700">{filhoAtual.class_name}</p>
              </div>
              {filhos.length > 1 && (
                <Badge variant="outline" className="bg-purple-200 text-purple-800 border-purple-300">
                  {filhos.length} filho(s)
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - AGORA COM 6 ABAS */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full overflow-x-auto justify-start gap-1 bg-muted p-1 rounded-lg">
          <TabsTrigger value="parciais" className="gap-1 text-xs sm:text-sm shrink-0">
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Notas Parciais</span>
            <span className="sm:hidden">Parciais</span>
          </TabsTrigger>
          <TabsTrigger value="boletim" className="gap-1 text-xs sm:text-sm shrink-0">
            <Award className="w-4 h-4" />
            <span className="hidden sm:inline">Boletim</span>
            <span className="sm:hidden">Boletim</span>
          </TabsTrigger>
          <TabsTrigger value="recuperacao" className="gap-1 text-xs sm:text-sm shrink-0" data-testid="tab-recuperacao">
            <LifeBuoy className="w-4 h-4" />
            <span className="hidden sm:inline">Recuperação</span>
            <span className="sm:hidden">Recup.</span>
          </TabsTrigger>
          <TabsTrigger value="horarios" className="gap-1 text-xs sm:text-sm shrink-0">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Horários</span>
            <span className="sm:hidden">Horários</span>
          </TabsTrigger>
          <TabsTrigger value="frequencia" className="gap-1 text-xs sm:text-sm shrink-0">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Frequência</span>
            <span className="sm:hidden">Freq.</span>
          </TabsTrigger>
          <TabsTrigger value="ocorrencias" className="gap-1 text-xs sm:text-sm shrink-0">
            <AlertCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Ocorrências</span>
            <span className="sm:hidden">Ocorr.</span>
          </TabsTrigger>
          <TabsTrigger value="avaliacoes" className="gap-1 text-xs sm:text-sm shrink-0">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Avaliacoes</span>
            <span className="sm:hidden">Aval.</span>
          </TabsTrigger>
          <TabsTrigger value="aulas-extras" className="gap-1 text-xs sm:text-sm shrink-0">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Aulas Extras</span>
            <span className="sm:hidden">Extras</span>
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* ============================================ */}
            {/* ABA: NOTAS PARCIAIS */}
            {/* ============================================ */}
            <TabsContent value="parciais" className="space-y-6">
              {/* Cards Explicativos dos Métodos Avaliativos */}
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-purple-600" />
                      Como calculamos as notas?
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowExplicacao(!showExplicacao)}
                      className="text-purple-600"
                    >
                      <HelpCircle className="w-4 h-4 mr-1" />
                      {showExplicacao ? 'Ocultar' : 'Ver detalhes'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    A <strong>Média Trimestral</strong> é calculada somando as 4 notas abaixo e dividindo por 4. Cada avaliação vale de <strong>0 a 10,0 pontos</strong>.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(TIPOS_AVALIACAO).map(([key, tipo]) => {
                      const Icon = tipo.icon;
                      return (
                        <div 
                          key={key}
                          className={`p-3 rounded-xl ${tipo.bg} ${tipo.border} border transition-all hover:shadow-md cursor-pointer`}
                          onClick={() => setShowExplicacao(true)}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tipo.cor} flex items-center justify-center mb-2`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className={`font-semibold text-sm ${tipo.text}`}>{tipo.nome}</p>
                          <p className="text-xs text-gray-500">Até {tipo.nota_max} pts</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Detalhes expandidos */}
                  {showExplicacao && (
                    <div className="mt-4 space-y-3 pt-4 border-t">
                      {Object.entries(TIPOS_AVALIACAO).map(([key, tipo]) => {
                        const Icon = tipo.icon;
                        return (
                          <div key={key} className={`p-4 rounded-lg ${tipo.bg} ${tipo.border} border`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${tipo.cor} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h4 className={`font-bold ${tipo.text}`}>{tipo.titulo}</h4>
                                <p className="text-sm text-gray-600 mt-1">{tipo.descricao}</p>
                                <Badge className={`mt-2 ${tipo.bg} ${tipo.text} border-0`}>
                                  Peso: {tipo.peso} • Máximo: {tipo.nota_max} pontos
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      <div className="p-4 bg-purple-100 rounded-lg border border-purple-300">
                        <div className="flex items-center gap-2 mb-2">
                          <Calculator className="w-5 h-5 text-purple-700" />
                          <h4 className="font-bold text-purple-900">Cálculo da Média Trimestral</h4>
                        </div>
                        <p className="text-sm text-purple-800">
                          <strong>Média = (APA + Prontidão + Trabalho + Av. Trimestral) ÷ 4</strong>
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          Exemplo: (8,0 + 7,5 + 9,0 + 8,5) ÷ 4 = 33,0 ÷ 4 = 8,25 → <strong>8,5</strong>
                        </p>
                        <p className="text-xs text-purple-500 mt-1 italic">
                          * A média é arredondada de 0,5 em 0,5 (ex: 7,3 → 7,5; 7,7 → 8,0)
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seletor de Trimestre */}
              <div className="flex items-center gap-4">
                <label className="font-medium text-gray-700">Trimestre:</label>
                <Select value={selectedTrimestre} onValueChange={setSelectedTrimestre}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1º Trimestre">1º Trimestre</SelectItem>
                    <SelectItem value="2º Trimestre">2º Trimestre</SelectItem>
                    <SelectItem value="3º Trimestre">3º Trimestre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notas Parciais por Disciplina */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ListChecks className="w-5 h-5" />
                    Notas Parciais - {selectedTrimestre}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {boletim?.subjects?.length > 0 ? (
                    <div className="space-y-4">
                      {boletim.subjects.map((subject) => {
                        const notas = getNotasParciais(subject.id);
                        const mediaParcial = calcularMediaTrimestral(notas);
                        
                        return (
                          <Card 
                            key={subject.id} 
                            className="hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => abrirDetalhesDisciplina(subject)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-800">{subject.name}</h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500">Prévia:</span>
                                  <span className={`text-lg font-bold px-3 py-1 rounded-lg border ${
                                    mediaParcial !== null ? getNotaColor(mediaParcial) : 'text-gray-400 bg-gray-50 border-gray-200'
                                  }`}>
                                    {mediaParcial !== null ? mediaParcial.toFixed(1) : '-'}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Grid de Notas Parciais */}
                              <div className="grid grid-cols-4 gap-2">
                                {Object.entries(TIPOS_AVALIACAO).map(([key, tipo]) => {
                                  const nota = notas[key];
                                  const Icon = tipo.icon;
                                  
                                  return (
                                    <div 
                                      key={key}
                                      className={`p-2 rounded-lg text-center ${tipo.bg} ${tipo.border} border`}
                                    >
                                      <Icon className={`w-4 h-4 mx-auto mb-1 ${tipo.text}`} />
                                      <p className="text-xs text-gray-600">{tipo.nome}</p>
                                      <p className={`text-lg font-bold ${nota !== undefined ? tipo.text : 'text-gray-400'}`}>
                                        {nota !== undefined ? nota.toFixed(1) : '-'}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Indicador de notas pendentes */}
                              {Object.keys(notas).length < 4 && (
                                <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                                  <Clock className="w-4 h-4" />
                                  <span>
                                    {4 - Object.keys(notas).length} nota(s) ainda não lançada(s)
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Nenhuma disciplina cadastrada para esta turma</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============================================ */}
            {/* ABA: BOLETIM (Médias Finais) */}
            {/* ============================================ */}
            <TabsContent value="boletim" className="space-y-6">
              <BoletimPremium boletim={boletim} filhoAtual={filhoAtual} selectedFilho={selectedFilho} />
            </TabsContent>

            {/* Recuperação */}
            <TabsContent value="recuperacao" className="space-y-4" data-testid="content-recuperacao">
              <RecuperacaoFamiliaTab selectedFilho={selectedFilho} filhoAtual={filhoAtual} />
            </TabsContent>

            {/* Frequência */}
            <TabsContent value="frequencia">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Frequência {frequencia?.year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {frequencia?.statistics ? (
                    <div className="space-y-6">
                      {/* Estatísticas */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-green-50 rounded-xl text-center">
                          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-green-700">{frequencia.statistics.presencas}</p>
                          <p className="text-sm text-green-600">Presenças</p>
                        </div>
                        <div className="p-4 bg-red-50 rounded-xl text-center">
                          <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-red-700">{frequencia.statistics.faltas}</p>
                          <p className="text-sm text-red-600">Faltas</p>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-xl text-center">
                          <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-yellow-700">{frequencia.statistics.justificadas}</p>
                          <p className="text-sm text-yellow-600">Justificadas</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-xl text-center">
                          {frequencia.statistics.percentual_presenca >= 75 ? (
                            <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                          ) : (
                            <TrendingDown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                          )}
                          <p className="text-2xl font-bold text-purple-700">{frequencia.statistics.percentual_presenca}%</p>
                          <p className="text-sm text-purple-600">Frequência</p>
                        </div>
                      </div>

                      {/* Alerta se frequência baixa */}
                      {frequencia.statistics.percentual_presenca < 75 && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Atenção: Frequência abaixo do mínimo!</span>
                          </div>
                          <p className="text-sm text-red-600 mt-1">
                            A frequência mínima exigida é de 75%. Entre em contato com a escola.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">Nenhum registro de frequência</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Ocorrências */}
            <TabsContent value="ocorrencias">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Ocorrências
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ocorrencias.length > 0 ? (
                    <div className="space-y-3">
                      {ocorrencias.map((ocorrencia, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge variant={ocorrencia.type === 'positive' ? 'default' : 'destructive'}>
                                {ocorrencia.type === 'positive' ? 'Positiva' : 'Negativa'}
                              </Badge>
                              <p className="mt-2 font-medium">{ocorrencia.title}</p>
                              <p className="text-sm text-gray-600 mt-1">{ocorrencia.description}</p>
                            </div>
                            <span className="text-sm text-gray-400">
                              {new Date(ocorrencia.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">Nenhuma ocorrência registrada</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============================================ */}
            {/* ABA: SEMANA DE AVALIAÇÕES */}
            {/* ============================================ */}
            <TabsContent value="avaliacoes" className="space-y-4">
              <SemanaAvaliacoesTab filhoId={selectedFilho} turmaId={filhoAtual?.class_group_id} />
            </TabsContent>

            {/* ============================================ */}
            {/* ABA: QUADRO DE HORÁRIOS */}
            {/* ============================================ */}
            <TabsContent value="horarios" className="space-y-4">
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      Quadro de Horários
                    </CardTitle>
                    {horarios?.turma && (
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                        {horarios.turma.name} - {horarios.turma.shift}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {horarios?.message ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>{horarios.message}</p>
                    </div>
                  ) : horarios?.schedules?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-purple-100">
                            <th className="border border-purple-200 px-2 py-2 text-left font-semibold text-purple-800 w-20">
                              Horário
                            </th>
                            {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'].map(dia => (
                              <th key={dia} className="border border-purple-200 px-2 py-2 text-center font-semibold text-purple-800">
                                <span className="hidden sm:inline">{dia}</span>
                                <span className="sm:hidden">{dia.slice(0, 3)}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(horarios?.time_slots || []).map((slot, slotIndex) => {
                            // Verificar se é intervalo (pausa)
                            const isIntervalo = slot.is_break || slot.label?.toLowerCase().includes('intervalo');
                            
                            if (isIntervalo) {
                              return (
                                <tr key={slotIndex} className="bg-amber-50">
                                  <td className="border border-purple-200 px-2 py-2 text-center font-medium text-amber-700" colSpan={6}>
                                    ☕ {slot.label || 'Intervalo'} ({slot.start} - {slot.end})
                                  </td>
                                </tr>
                              );
                            }
                            
                            return (
                              <tr key={slotIndex} className="hover:bg-purple-50">
                                <td className="border border-purple-200 px-2 py-2 text-center bg-purple-50">
                                  <div className="font-medium text-purple-700">{slot.start}</div>
                                  <div className="text-xs text-purple-500">{slot.end}</div>
                                </td>
                                {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'].map(dia => {
                                  // Encontrar aula para este horário e dia
                                  const aula = horarios.schedules.find(s => 
                                    s.day_of_week === dia && 
                                    (s.time_slot_index === slotIndex || s.start_time === slot.start)
                                  );
                                  
                                  if (aula) {
                                    // Cores por disciplina
                                    const cores = [
                                      'bg-blue-100 border-blue-300 text-blue-800',
                                      'bg-green-100 border-green-300 text-green-800',
                                      'bg-orange-100 border-orange-300 text-orange-800',
                                      'bg-pink-100 border-pink-300 text-pink-800',
                                      'bg-cyan-100 border-cyan-300 text-cyan-800',
                                      'bg-indigo-100 border-indigo-300 text-indigo-800',
                                      'bg-yellow-100 border-yellow-300 text-yellow-800',
                                      'bg-rose-100 border-rose-300 text-rose-800',
                                    ];
                                    const corIndex = (aula.subject_name?.charCodeAt(0) || 0) % cores.length;
                                    
                                    return (
                                      <td key={dia} className={`border border-purple-200 px-1 py-1`}>
                                        <div className={`rounded-lg p-2 text-center ${cores[corIndex]} border`}>
                                          <div className="font-semibold text-xs truncate" title={aula.subject_name}>
                                            {aula.subject_name || 'Disciplina'}
                                          </div>
                                          <div className="text-xs opacity-75 truncate mt-0.5" title={aula.teacher_name}>
                                            {aula.teacher_name || ''}
                                          </div>
                                        </div>
                                      </td>
                                    );
                                  }
                                  
                                  return (
                                    <td key={dia} className="border border-purple-200 px-1 py-1">
                                      <div className="text-center text-gray-300 py-2">-</div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Quadro de horários ainda não foi definido para esta turma</p>
                    </div>
                  )}
                  
                  {/* Legenda */}
                  {horarios?.schedules?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <p className="text-xs text-gray-500 text-center">
                        Quadro de horários da turma <strong>{horarios.turma?.name}</strong> - Ano {horarios.turma?.year}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="aulas-extras" className="space-y-4">
              <AulasExtrasFamilia selectedFilho={selectedFilho} filhoAtual={filhoAtual} />
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Modal de Detalhes da Disciplina */}
      <Dialog open={!!selectedDisciplina} onOpenChange={() => setSelectedDisciplina(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              {selectedDisciplina?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDisciplina && (() => {
            const notas = getNotasParciais(selectedDisciplina.id);
            const media = calcularMediaTrimestral(notas);
            
            return (
              <div className="space-y-4">
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <p className="text-sm text-purple-600 mb-1">Prévia da Média - {selectedTrimestre}</p>
                  <p className={`text-4xl font-bold ${media !== null ? (media >= 7 ? 'text-green-600' : media >= 5 ? 'text-yellow-600' : 'text-red-600') : 'text-gray-400'}`}>
                    {media !== null ? media.toFixed(1) : '-'}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {Object.entries(TIPOS_AVALIACAO).map(([key, tipo]) => {
                    const nota = notas[key];
                    const Icon = tipo.icon;
                    
                    return (
                      <div key={key} className={`flex items-center justify-between p-3 rounded-lg ${tipo.bg} ${tipo.border} border`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tipo.cor} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className={`font-medium ${tipo.text}`}>{tipo.titulo}</p>
                            <p className="text-xs text-gray-500">Máximo: {tipo.nota_max} pts</p>
                          </div>
                        </div>
                        <span className={`text-2xl font-bold ${nota !== undefined ? tipo.text : 'text-gray-400'}`}>
                          {nota !== undefined ? nota.toFixed(1) : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="p-3 bg-gray-100 rounded-lg text-center">
                  <p className="text-xs text-gray-500">
                    {4 - Object.keys(notas).length > 0 
                      ? `Aguardando ${4 - Object.keys(notas).length} nota(s) para completar o trimestre`
                      : 'Todas as notas do trimestre foram lançadas'}
                  </p>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// Aba "Recuperação" — DNA Connect (família)
// ─────────────────────────────────────────────────────────────────
function RecuperacaoFamiliaTab({ selectedFilho, filhoAtual }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedFilho) return;
    const token = localStorage.getItem('dnaconnect_token') || localStorage.getItem('familyToken') || '';
    setLoading(true);
    fetch(`${API_URL}/api/dnaconnect/pedagogico/recuperacao/${selectedFilho}?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [selectedFilho]);

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-pink-600" /></div>;
  }

  if (!items.length) {
    return (
      <Card>
        <CardContent className="pt-6 text-center text-slate-500 text-sm">
          <LifeBuoy className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nenhum plano de recuperação enviado para {filhoAtual?.name || 'este aluno'}.
        </CardContent>
      </Card>
    );
  }

  const STATUS_META = {
    aberta: { label: 'Aberta', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    enviada: { label: 'Enviada', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    concluida: { label: 'Concluída', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  };

  return (
    <div className="space-y-3" data-testid="recuperacao-familia-list">
      {items.map((it) => {
        const meta = STATUS_META[it.status] || STATUS_META.enviada;
        return (
          <Card key={it.id} className="border-pink-100">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <LifeBuoy className="w-4 h-4 text-pink-600" />
                    {it.title || `Recuperação — ${it.subject_name}`}
                  </CardTitle>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {it.subject_name} · {it.trimester} · Prof. {it.teacher_name || '—'}
                  </div>
                </div>
                <Badge variant="outline" className={`${meta.cls} text-xs`}>{meta.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              {it.instructions && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Objetivo / Instruções</div>
                  <div className="text-slate-700 whitespace-pre-wrap">{it.instructions}</div>
                </div>
              )}
              {Array.isArray(it.study_contents) && it.study_contents.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Conteúdos para estudar</div>
                  <ul className="list-disc list-inside text-slate-700">
                    {it.study_contents.map((c, idx) => (
                      <li key={idx}><b>[{c.tipo}]</b> {c.valor}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                <span>Enviado em {it.created_at ? new Date(it.created_at).toLocaleDateString('pt-BR') : '—'}</span>
                <span>Prazo: {it.due_date ? new Date(it.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
